import { create } from "zustand";
import type { components } from "@/lib/api/types";
import { clearAuthData, loadAuthData, saveAuthData } from "@/lib/auth/storage";

type UserResponse = components["schemas"]["UserResponse"];

export interface AuthState {
	// State
	user: UserResponse | null;
	accessToken: string | null;
	refreshToken: string | null;
	accessExpiresAt: string | null;
	refreshExpiresAt: string | null;
	isLoading: boolean;
	isInitialized: boolean;
	isRestoring: boolean;

	// Computed
	isAuthenticated: boolean;

	// Actions
	setAuth: (data: {
		user: UserResponse;
		accessToken: string;
		refreshToken: string;
		accessExpiresAt: string;
		refreshExpiresAt: string;
	}) => void;
	setTokens: (data: {
		accessToken: string;
		refreshToken: string;
		accessExpiresAt: string;
		refreshExpiresAt: string;
	}) => void;
	setUser: (user: UserResponse) => void;
	clearAuth: () => void;
	setLoading: (loading: boolean) => void;
	restoreAuth: () => void;
	initialize: () => void;
}

// Helper to check if we're on the client (not SSR)
const isClient = typeof window !== "undefined";

// Promise that resolves when auth initialization is complete
let initPromise: Promise<void> | null = null;
let initResolver: (() => void) | null = null;

// Create the initialization promise immediately
initPromise = new Promise<void>((resolve) => {
	initResolver = resolve;
});

// Helper to get the initialization promise
export function getAuthInitPromise(): Promise<void> {
	return initPromise || Promise.resolve();
}

export const useAuthStore = create<AuthState>(
	(set, get): AuthState => ({
		// Start with empty state - will be initialized on client only
		user: null,
		accessToken: null,
		refreshToken: null,
		accessExpiresAt: null,
		refreshExpiresAt: null,
		isLoading: false,
		// During SSR, mark as initialized immediately (no auth on server)
		// On client, wait for actual initialization
		isInitialized: !isClient,
		isRestoring: false,
		isAuthenticated: false,

		// Set full auth data (login/signup)
		setAuth: (data) => {
			set({
				user: data.user,
				accessToken: data.accessToken,
				refreshToken: data.refreshToken,
				accessExpiresAt: data.accessExpiresAt,
				refreshExpiresAt: data.refreshExpiresAt,
				isAuthenticated: true,
			});

			// Persist to localStorage
			saveAuthData(data);
		},

		// Update user and persist to localStorage
		setUser: (user) => {
			const { accessToken, refreshToken, accessExpiresAt, refreshExpiresAt } = get();
			set({ user });
			if (accessToken && refreshToken && accessExpiresAt && refreshExpiresAt) {
				saveAuthData({ user, accessToken, refreshToken, accessExpiresAt, refreshExpiresAt });
			}
		},

		// Update only tokens (refresh)
		setTokens: (data) => {
			const { user } = get();
			if (!user) {
				console.warn("Cannot set tokens without user");
				return;
			}

			set({
				accessToken: data.accessToken,
				refreshToken: data.refreshToken,
				accessExpiresAt: data.accessExpiresAt,
				refreshExpiresAt: data.refreshExpiresAt,
			});

			// Update localStorage
			saveAuthData({ ...data, user });
		},

		// Clear auth (logout)
		clearAuth: () => {
			set({
				user: null,
				accessToken: null,
				refreshToken: null,
				accessExpiresAt: null,
				refreshExpiresAt: null,
				isAuthenticated: false,
			});

			// Clear localStorage
			clearAuthData();
		},

		// Set loading state
		setLoading: (loading) => {
			set({ isLoading: loading });
		},

		// Restore auth from localStorage
		restoreAuth: () => {
			const data = loadAuthData();

			if (data) {
				set({
					user: data.user,
					accessToken: data.accessToken,
					refreshToken: data.refreshToken,
					accessExpiresAt: data.accessExpiresAt,
					refreshExpiresAt: data.refreshExpiresAt,
					isAuthenticated: true,
					isInitialized: true,
					isRestoring: false,
				});
			} else {
				set({ isInitialized: true, isRestoring: false });
			}
		},

		// Initialize auth state from localStorage
		// Only runs on client-side (skipped during SSR)
		initialize: () => {
			// Skip initialization on server-side
			if (!isClient) {
				// On server, just mark as initialized and resolve promise
				set({ isInitialized: true });
				if (initResolver) {
					initResolver();
					initResolver = null;
				}
				return;
			}

			// Client-side: load from localStorage
			const data = loadAuthData();

			if (data) {
				set({
					user: data.user,
					accessToken: data.accessToken,
					refreshToken: data.refreshToken,
					accessExpiresAt: data.accessExpiresAt,
					refreshExpiresAt: data.refreshExpiresAt,
					isAuthenticated: true,
					isInitialized: true,
				});
			} else {
				set({ isInitialized: true });
			}

			// Resolve the initialization promise
			if (initResolver) {
				initResolver();
				initResolver = null;
			}
		},
	})
);

// Initialize the store immediately after creation
// On client: loads from localStorage
// On server: no-op (already marked as initialized)
useAuthStore.getState().initialize();
